import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Mail, MailOpen, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';

const AdminMessages = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setMessages(data);
    }
    setLoading(false);
  };

  const formatMessageStatus = (status) => {
    switch (status) {
      case 'new': return 'Nouveau';
      case 'read': return 'Lu';
      case 'replied': return 'Répondu';
      default: return status;
    }
  };

  const updateStatus = async (id, status) => {
    const { error } = await supabase
      .from('messages')
      .update({ status })
      .eq('id', id);

    if (!error) {
      fetchMessages();
      toast({
        title: "Statut mis à jour",
        description: `Message marqué comme ${formatMessageStatus(status).toLowerCase()}`
      });
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce message ?")) return;

    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', id);

    if (!error) {
      fetchMessages();
      setSelectedMessage(null);
      toast({
        title: "Message supprimé",
        description: "Le message a été supprimé avec succès"
      });
    }
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Messages - Admin - Bigadsy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-1">Messages</h1>
          <p className="text-sm text-slate-500">Gérez les demandes de contact et prospects entrants</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3"></div>
            <p className="text-sm">Chargement des messages...</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Messages List */}
            <div className="lg:col-span-1 space-y-3">
              {messages.length === 0 ? (
                <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-400 text-sm">
                  Aucun message trouvé
                </div>
              ) : (
                messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setSelectedMessage(message)}
                    className={`p-4 rounded-xl cursor-pointer transition-all border ${selectedMessage?.id === message.id
                        ? 'bg-blue-50/70 border-blue-500 shadow-sm ring-1 ring-blue-500'
                        : 'bg-white hover:bg-slate-50 border-slate-200 shadow-xs'
                      }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {message.status === 'new' ? (
                          <Mail className="h-4 w-4 text-blue-600 shrink-0" />
                        ) : (
                          <MailOpen className="h-4 w-4 text-slate-400 shrink-0" />
                        )}
                        <span className="font-semibold text-slate-900 text-sm truncate max-w-[140px]">{message.name}</span>
                      </div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize ${message.status === 'new'
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : message.status === 'replied'
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                            : 'bg-slate-100 text-slate-600 border border-slate-200'
                        }`}>
                        {formatMessageStatus(message.status)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{message.message}</p>
                    <p className="text-xs text-slate-400 mt-2 font-medium">
                      {new Date(message.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </motion.div>
                ))
              )}
            </div>

            {/* Message Detail */}
            <div className="lg:col-span-2">
              {selectedMessage ? (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-xs"
                >
                  <div className="flex items-start justify-between mb-6 pb-4 border-b border-slate-100">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 mb-1">{selectedMessage.name}</h2>
                      <p className="text-sm font-medium text-blue-600">{selectedMessage.email}</p>
                      {selectedMessage.phone && (
                        <p className="text-xs text-slate-500 mt-0.5">{selectedMessage.phone}</p>
                      )}
                    </div>
                    <Button
                      onClick={() => deleteMessage(selectedMessage.id)}
                      variant="outline"
                      size="sm"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
                    </Button>
                  </div>

                  {selectedMessage.service && (
                    <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-0.5">Service souhaité</span>
                      <p className="text-sm font-semibold text-slate-800">{selectedMessage.service}</p>
                    </div>
                  )}

                  <div className="mb-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-2">Message</span>
                    <div className="p-4 bg-slate-50/70 border border-slate-200 rounded-xl text-slate-800 text-sm whitespace-pre-wrap leading-relaxed">
                      {selectedMessage.message}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    {selectedMessage.status === 'new' && (
                      <Button
                        onClick={() => updateStatus(selectedMessage.id, 'read')}
                        className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white font-medium rounded-xl shadow-xs"
                      >
                        Marquer comme lu
                      </Button>
                    )}
                    {selectedMessage.status === 'read' && (
                      <Button
                        onClick={() => updateStatus(selectedMessage.id, 'replied')}
                        className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white font-medium rounded-xl shadow-xs"
                      >
                        Marquer comme répondu
                      </Button>
                    )}
                  </div>
                </motion.div>
              ) : (
                <div className="bg-white rounded-2xl p-12 border border-dashed border-slate-300 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-7 w-7" />
                  </div>
                  <h3 className="text-base font-bold text-slate-800 mb-1">Aucun message sélectionné</h3>
                  <p className="text-sm text-slate-400">Sélectionnez une demande dans la liste à gauche pour voir les détails</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;